// src/components/chat/Composer.tsx
import React, { useRef } from "react";
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Send,
  PhotoCamera,
  Explore,
  AttachFile,
} from "@mui/icons-material";
import { useT } from '../../i18n/useT';

/**
 * Maximum chars the composer accepts. Mirrors MAX_MESSAGE_LENGTH
 * on the server (functions2/src/streamTourGuide.ts) — keep in
 * sync. The server is authoritative; this just keeps the UX
 * honest so users don't compose 50KB of text and then hit a 413.
 *
 * 8000 is well past anything a real human types; anything beyond
 * is paste-spam or abuse.
 */
const MAX_COMPOSER_LENGTH = 8000;

interface ComposerProps {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  onOpenCamera: () => void;
  onExplore: () => void;
  /** Called when user picks images from file picker. May be multiple. */
  onAttachFiles: (files: FileList) => void;
  /** Called when user taps a suggestion chip — fills + focuses composer. */
  onPickSuggestion: (text: string) => void;
  /** when true, send is enabled even if value is empty (image-only send) */
  canSendEmpty: boolean;
  disabled: boolean;
  isStreaming: boolean;
  isExploring: boolean;
}

export const Composer: React.FC<ComposerProps> = ({
  value,
  onChange,
  onSend,
  onOpenCamera,
  onExplore,
  onAttachFiles,
  onPickSuggestion,
  canSendEmpty,
  disabled,
  isStreaming,
  isExploring,
}) => {
  const t = useT();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const placeholder = isStreaming
    ? t("Thinking…")
    : isExploring
    ? t("Getting your location…")
    : t("Ask what you're looking at, or where you are");

  const sendEnabled = !disabled && (value.trim().length > 0 || canSendEmpty);

  const handleFilesPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onAttachFiles(e.target.files);
      // Reset so the same file can be picked again
      e.target.value = "";
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mx: 2,
        mb: 2,
        p: 1,
        borderRadius: 3,
        border: 1,
        borderColor: "divider",
        flexShrink: 0,
        bgcolor: "background.paper",
      }}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFilesPicked}
        style={{ display: "none" }}
      />
      <Box sx={{ display: "flex", gap: 0.5, alignItems: "flex-end" }}>
        <Tooltip title={t("Attach photos")}>
          <span>
            <IconButton
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled}
              size="medium"
              sx={{ color: "text.secondary" }}
              aria-label={t("Attach photos")}
            >
              <AttachFile />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={t("Take a photo")}>
          <span>
            <IconButton
              onClick={onOpenCamera}
              disabled={disabled}
              size="medium"
              sx={{ color: "text.secondary" }}
              aria-label={t("Take a photo")}
            >
              <PhotoCamera />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={t("Explore where I am")}>
          <span>
            <IconButton
              onClick={onExplore}
              disabled={disabled || isExploring}
              size="medium"
              sx={{ color: "text.secondary" }}
              aria-label={t("Explore this area")}
            >
              {isExploring ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Explore />
              )}
            </IconButton>
          </span>
        </Tooltip>

        <TextField
          fullWidth
          multiline
          maxRows={5}
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            // Hard cap matches the server-side cap in
            // streamTourGuide.ts (MAX_MESSAGE_LENGTH = 8000).
            // We truncate on input rather than just rejecting
            // submission so the user can't get into a state
            // where the visible text is longer than what the
            // server will accept.
            const next = e.target.value;
            if (next.length > MAX_COMPOSER_LENGTH) {
              onChange(next.slice(0, MAX_COMPOSER_LENGTH));
            } else {
              onChange(next);
            }
          }}
          onKeyDown={handleKey}
          disabled={disabled || isExploring}
          variant="standard"
          inputProps={{
            // Defense in depth — browser-enforced cap on top of
            // the onChange truncate. A user pasting beyond the
            // limit hits this first and never sees the
            // truncate.
            maxLength: MAX_COMPOSER_LENGTH,
          }}
          InputProps={{
            disableUnderline: true,
            sx: { px: 1, py: 0.5, fontSize: 15 },
          }}
          helperText={
            value.length > MAX_COMPOSER_LENGTH * 0.9
              ? `${value.length} / ${MAX_COMPOSER_LENGTH}`
              : undefined
          }
          FormHelperTextProps={{
            sx: {
              ml: 1,
              fontSize: 11,
              color:
                value.length >= MAX_COMPOSER_LENGTH
                  ? 'error.main'
                  : 'text.secondary',
            },
          }}
        />

        <IconButton
          color="primary"
          onClick={onSend}
          disabled={!sendEnabled}
          size="medium"
          aria-label={t("Send")}
          sx={{
            bgcolor: sendEnabled ? "primary.main" : "transparent",
            color: sendEnabled ? "primary.contrastText" : "text.disabled",
            "&:hover": {
              bgcolor: sendEnabled ? "primary.dark" : "transparent",
            },
            transition: "background-color 0.15s ease",
            width: 40,
            height: 40,
          }}
        >
          <Send fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
};
